import React, { useState, useCallback, useEffect } from 'react';
import { useNotificationSettings } from '../../hooks/useNotificationSettings';
import ToggleSwitch from '../ui/ToggleSwitch';
import TimePicker from '../ui/TimePicker';
import FrequencySelector from './FrequencySelector';
import NotificationPresets from './NotificationPresets';
import { getFrequencyConfig } from './frequencyConfig';
import { 
  IoNotifications, 
  IoMail, 
  IoFlag, 
  IoTime, 
  IoVolumeOff, 
  IoChevronDown,
  IoSettings
} from 'react-icons/io5';

const notificationCategories = [
  {
    id: 'documentation',
    title: '📝 Documentation & Progress',
    description: 'Reminders to document your work and track progress',
    borderColor: 'border-blue-500',
    types: [
      {
        key: 'documentation_reminders' as const,
        label: 'Documentation reminders',
        description: 'Enable badges, banners, and priority notifications for documentation reminders. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'progress_summaries' as const,
        label: 'Progress summaries',
        description: 'Enable badges, banners, and priority notifications for progress summaries. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'self_review' as const,
        label: 'Self Review',
        description: 'Enable badges, banners, and priority notifications for self-review reminders. When disabled, notifications appear only in "All" tab at lowest frequency.'
      }
    ]
  },
  {
    id: 'goals',
    title: '🎯 Goals & Planning',
    description: 'Help with goal setting and career planning',
    borderColor: 'border-green-500',
    types: [
      {
        key: 'goal_setting_prompts' as const,
        label: 'Goal setting prompts',
        description: 'Enable badges, banners, and priority notifications for goal setting prompts. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'career_opportunities' as const,
        label: 'Career opportunities',
        description: 'Enable badges, banners, and priority notifications for career opportunities and suggestions. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'reflection_reminders' as const,
        label: 'Reflection reminders',
        description: 'Enable badges, banners, and priority notifications for reflection reminders. When disabled, notifications appear only in "All" tab at lowest frequency.'
      }
    ]
  },
  {
    id: 'achievements',
    title: '🏆 Achievements & Milestones',
    description: 'Celebrate your wins and track milestones',
    borderColor: 'border-yellow-500',
    types: [
      {
        key: 'achievement_celebrations' as const,
        label: 'Achievement celebrations',
        description: 'Enable badges, banners, and priority notifications for achievement celebrations. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'milestone_celebrations' as const,
        label: 'Milestone celebrations',
        description: 'Enable badges, banners, and priority notifications for milestone celebrations. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'streak_reminders' as const,
        label: 'Streak reminders',
        description: 'Enable badges, banners, and priority notifications for streak reminders. When disabled, notifications appear only in "All" tab at lowest frequency.'
      }
    ]
  },
  {
    id: 'internship',
    title: '💼 Internship Tracking',
    description: 'Stay on track with internship progress and reminders',
    borderColor: 'border-purple-500',
    types: [
      {
        key: 'weekly_assessment_reminder' as const,
        label: 'Weekly assessment reminders',
        description: 'Enable badges, banners, and priority notifications for weekly assessment reminders. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'weekly_reflection_prompt' as const,
        label: 'Weekly reflection prompts',
        description: 'Enable badges, banners, and priority notifications for weekly reflection prompts. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'internship_goal_achieved' as const,
        label: 'Goal achievement celebrations',
        description: 'Enable badges, banners, and priority notifications when you achieve internship goals. When disabled, notifications appear only in "All" tab at lowest frequency.'
      },
      {
        key: 'internship_phase_transition' as const,
        label: 'Phase transition updates',
        description: 'Enable badges, banners, and priority notifications when you enter a new internship phase. When disabled, notifications appear only in "All" tab at lowest frequency.'
      }
    ]
  }
];

const UnifiedNotificationSettings: React.FC = () => {
  const {
    settings,
    isLoading,
    error,
    toggleDeliveryMethod,
    updateScheduledSettings,
    updateNotificationFrequency,
    isDeliveryMethodEnabled,
    getNotificationFrequency,
    isNotificationTypeEnabled,
    refreshSettings
  } = useNotificationSettings();

  const [expandedSections, setExpandedSections] = useState({
    delivery: true,
    scheduled: true,
    advanced: false
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const [forceRenderKey, setForceRenderKey] = useState(0);

  // Watch for changes in notification_types and force re-render
  useEffect(() => {
    if (settings?.scheduled_notifications?.notification_types) {
      setForceRenderKey(prev => prev + 1);
    }
  }, [settings?.scheduled_notifications?.notification_types]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleTypeToggle = async (type: string, enabled: boolean) => {
    if (!settings?.scheduled_notifications) return;

    try {
      const updatedTypes = {
        ...settings.scheduled_notifications.notification_types,
        [type]: enabled
      };

      await updateScheduledSettings({
        notification_types: updatedTypes
      });
    } catch (error) {
      console.error('Error updating notification type:', error);
    }
  };

  const handleFrequencyChange = async (type: string, frequency: string) => {
    await updateNotificationFrequency(type as any, frequency);
  };

  const handlePresetApplied = useCallback(async () => {
    setForceRenderKey(prev => prev + 1);
    // Also refresh settings to ensure we have the latest state
    await refreshSettings();
    // Add a small delay to allow React to process the state updates
    setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 100);
  }, [refreshSettings]);

  useEffect(() => {
    if (settings?.scheduled_notifications) {
      setForceRenderKey(prev => prev + 1);
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
      }, 100);
    }
  }, [settings?.scheduled_notifications]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-3"></div>
            <div className="space-y-3">
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-600 dark:text-red-300 text-sm">{error}</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-gray-600 dark:text-gray-400">No notification settings available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delivery Methods */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => toggleSection('delivery')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center">
            <IoNotifications className="w-5 h-5 text-blue-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Delivery Methods
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Choose how you want to receive notifications
              </p>
            </div>
          </div>
          <IoChevronDown 
            className={`w-5 h-5 text-gray-500 transition-transform ${
              expandedSections.delivery ? 'rotate-180' : ''
            }`}
          />
        </button>
        
        {expandedSections.delivery && (
          <div className="px-4 pb-4 space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <IoNotifications className="w-4 h-4 text-blue-500 mr-3" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm">
                    In-app notifications
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Show all notifications in the app (bell icon and banners)
                  </div>
                </div>
              </div>
              <ToggleSwitch
                enabled={isDeliveryMethodEnabled('bell')}
                onChange={() => toggleDeliveryMethod('bell')}
                size="sm"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <IoMail className="w-4 h-4 text-green-500 mr-3" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm">
                    Email notifications
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Send notifications to your email address
                  </div>
                </div>
              </div>
              <ToggleSwitch
                enabled={isDeliveryMethodEnabled('email')}
                onChange={() => toggleDeliveryMethod('email')}
                size="sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Scheduled Notifications */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => toggleSection('scheduled')}
            className="flex items-center flex-1 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg p-2 -m-2"
          >
            <IoTime className="w-5 h-5 text-blue-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Scheduled Notifications
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Periodic reminders and summaries to help you stay on track
              </p>
            </div>
            <IoChevronDown 
              className={`w-5 h-5 text-gray-500 transition-transform ml-auto ${
                expandedSections.scheduled ? 'rotate-180' : ''
              }`}
            />
          </button>
          <div className="ml-3">
            <ToggleSwitch
              enabled={settings.scheduled_notifications.enabled}
              onChange={(enabled) => updateScheduledSettings({ enabled })}
              size="sm"
            />
          </div>
        </div>
        
        {expandedSections.scheduled && settings.scheduled_notifications.enabled && (
          <div className="px-4 pb-4 space-y-6">
            {/* Quick Setup Presets */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Quick Setup
              </h4>
              <NotificationPresets onPresetApplied={handlePresetApplied} />
            </div>

            {/* Basic Settings */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Basic Settings
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <TimePicker
                  label="Preferred time"
                  value={settings.scheduled_notifications.preferred_time}
                  onChange={(time) => updateScheduledSettings({ preferred_time: time })}
                />
              </div>

              <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center">
                  <IoVolumeOff className="w-4 h-4 text-gray-500 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      Quiet hours
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      No notifications during these hours
                    </div>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={settings.scheduled_notifications.quiet_hours_enabled}
                  onChange={(enabled) => updateScheduledSettings({ quiet_hours_enabled: enabled })}
                  size="sm"
                />
              </div>

              {settings.scheduled_notifications.quiet_hours_enabled && (
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <TimePicker
                    label="Start time"
                    value={settings.scheduled_notifications.quiet_hours_start}
                    onChange={(time) => updateScheduledSettings({ quiet_hours_start: time })}
                  />
                  <TimePicker
                    label="End time"
                    value={settings.scheduled_notifications.quiet_hours_end}
                    onChange={(time) => updateScheduledSettings({ quiet_hours_end: time })}
                  />
                </div>
              )}
            </div>

            {/* Active Alerts */}
            <div key={`${refreshKey}-${JSON.stringify(settings.scheduled_notifications.notification_types)}`}>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Active Alerts
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                Control which notifications actively alert you with badges, banners, and your chosen delivery methods. 
                Disabled notifications will still appear in your "All" notifications tab at the lowest frequency. 
                Critical notifications (security, onboarding) always bypass these settings.
              </p>
              <div className="space-y-4">
                {notificationCategories.map(category => (
                  <div key={category.id} className={`border-l-4 ${category.borderColor} pl-4 bg-gray-50 dark:bg-gray-700/50 rounded-r-lg p-3`}>
                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                      {category.title}
                    </h5>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      {category.description}
                    </p>
                    
                    <div className="space-y-3">
                      {category.types.map(type => {
                        // Read enabled state directly from settings for more immediate updates
                        const isEnabled = settings.scheduled_notifications.notification_types?.[type.key] ?? false;
                        const frequencyConfig = getFrequencyConfig(type.key);
                        const currentFrequency = getNotificationFrequency(type.key as any);
                        
                        // Debug logging
                        // console.log(`Type ${type.key}: enabled=${isEnabled}, forceRenderKey=${forceRenderKey}`);
                        
                        return (
                          <div key={type.key} className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <ToggleSwitch
                                  key={`${type.key}-${isEnabled}-${forceRenderKey}`}
                                  label={type.label}
                                  description={type.description}
                                  enabled={isEnabled}
                                  onChange={(enabled) => handleTypeToggle(type.key, enabled)}
                                  size="sm"
                                />
                              </div>
                            </div>
                            
                            {isEnabled && frequencyConfig && (
                              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex items-center gap-3">
                                  <FrequencySelector
                                    value={currentFrequency || frequencyConfig.default}
                                    onChange={(frequency) => handleFrequencyChange(type.key, frequency)}
                                    options={frequencyConfig.options}
                                    label="Frequency"
                                    size="sm"
                                    className="flex-1 max-w-xs"
                                  />
                                  <div className="text-xs text-gray-500 dark:text-gray-400 flex-1">
                                    {frequencyConfig.options.find(opt => opt.value === (currentFrequency || frequencyConfig.default))?.description}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedNotificationSettings; 